import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { DataService } from "../../Services/dataService";
import Loader from "../../Components/Loader";
import {
  RateRequestProfile,
  collectRateRequests,
  quoteSummary,
} from "./RateRequestProfile";

export const RateApprovalList = () => {
  const history = useHistory();
  const [listData, setListData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [tab, setTab] = useState("requested");
  const [actionRetailer, setActionRetailer] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchList();
  }, [tab]);

  const fetchList = () => {
    setLoader(true);
    DataService.get("/admin/get_rate_approval_requests", { tab, limit: 100, flatten: 1 })
      .then((res) => {
        if (res && res.status) {
          setListData(res.data || []);
          setTotal((res.pagination && res.pagination.total) || 0);
        }
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };

  const openAction = (id, mode) => {
    setActionRetailer({ id, mode });
    setMessage("");
  };

  const closeAction = () => {
    setActionRetailer(null);
    setMessage("");
    setSubmitting(false);
  };

  const submitAction = () => {
    if (!actionRetailer) return;
    if (actionRetailer.mode === "reject" && !message.trim()) {
      alert("Please enter a message for the retailer.");
      return;
    }

    setSubmitting(true);
    const endpoint =
      actionRetailer.mode === "approve"
        ? "/admin/approve_retailer_rate"
        : "/admin/reject_retailer_rate";

    DataService.post(endpoint, {
      retailer_id: actionRetailer.id,
      message: message.trim(),
    })
      .then((res) => {
        setSubmitting(false);
        if (res && res.status) {
          alert(res.message || "Done");
          closeAction();
          fetchList();
        } else {
          alert((res && res.message) || "Action failed");
        }
      })
      .catch(() => {
        setSubmitting(false);
        alert("An error occurred");
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusBadge = (status) => {
    const map = {
      requested: { bg: "#fff3cd", color: "#856404", border: "#ffc107", label: "Requested" },
      pending: { bg: "#f8d7da", color: "#842029", border: "#f5c2c7", label: "Not requested" },
      rejected: { bg: "#fde8e8", color: "#9b1c1c", border: "#f8b4b4", label: "Needs update" },
      approved: { bg: "#d1e7dd", color: "#0f5132", border: "#badbcc", label: "Approved" },
    };
    const s = map[status] || map.pending;
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
          padding: "3px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        {s.label}
      </span>
    );
  };

  const tabs = [
    { id: "requested", label: "Requested" },
    { id: "rejected", label: "Needs update" },
    { id: "pending", label: "Not requested" },
    { id: "approved", label: "Approved" },
  ];

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div>
                <h4 className="card-title" style={{ marginBottom: "4px" }}>
                  Rate Approval Requests
                </h4>
                <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>
                  Each rate request is listed separately (e.g. Riyadh and Mumbai = 2 rows). Expand for full
                  details, or open Profile for all requests of that retailer.
                </p>
              </div>
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#856404",
                }}
              >
                {total} record{total !== 1 ? "s" : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    border: tab === t.id ? "1px solid #038559" : "1px solid #dee2e6",
                    background: tab === t.id ? "#038559" : "#fff",
                    color: tab === t.id ? "#fff" : "#495057",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loader ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Loader />
              </div>
            ) : listData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d" }}>
                <h5>No records in this tab</h5>
                <p>Switch tabs to review other retailers.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover" style={{ borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f8f9fa" }}>
                    <tr>
                      <th style={{ width: "40px" }}></th>
                      <th>#</th>
                      <th>Retailer</th>
                      <th>Lane / Quote</th>
                      <th>Total payable</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listData.map((item, idx) => {
                      // Flattened API: each row is one rate request
                      const thisRequest = item.rate_request_data;
                      const summary = quoteSummary(thisRequest);
                      const rowKey = item.row_key || `${item._id}_${item.request_id || idx}`;
                      const requestedAt =
                        (thisRequest && (thisRequest.submitted_at || thisRequest.requested_at)) ||
                        item.updated_at ||
                        item.created_at;
                      const reqCount = item.total_requests || collectRateRequests(item).length || 0;
                      return (
                        <React.Fragment key={rowKey}>
                          <tr>
                            <td
                              onClick={() =>
                                setExpandedRow(expandedRow === rowKey ? null : rowKey)
                              }
                              style={{
                                textAlign: "center",
                                color: "#0323fe",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {expandedRow === rowKey ? "▲" : "▼"}
                            </td>
                            <td>{idx + 1}</td>
                            <td>
                              <strong>{item.retailer_name || "(No name)"}</strong>
                              <br />
                              <small style={{ color: "#6c757d" }}>{item.retailer_email}</small>
                              <br />
                              <small style={{ color: "#6c757d" }}>{item.phone_number || "-"}</small>
                              {reqCount > 1 && (
                                <>
                                  <br />
                                  <small style={{ color: "#084298", fontWeight: 700 }}>
                                    {reqCount} requests total
                                  </small>
                                </>
                              )}
                            </td>
                            <td>
                              <div style={{ fontSize: "13px", fontWeight: "600" }}>{summary.lane}</div>
                              <small style={{ color: "#6c757d" }}>{summary.courier}</small>
                              {item.is_latest_request && (
                                <>
                                  <br />
                                  <span
                                    style={{
                                      background: "#d1e7dd",
                                      color: "#0f5132",
                                      borderRadius: "8px",
                                      padding: "1px 8px",
                                      fontSize: "10px",
                                      fontWeight: 700,
                                    }}
                                  >
                                    Latest
                                  </span>
                                </>
                              )}
                            </td>
                            <td style={{ fontWeight: "700", color: "#28a745" }}>{summary.total}</td>
                            <td>{statusBadge(item.rate_approval_status)}</td>
                            <td>{formatDate(requestedAt)}</td>
                            <td>
                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                {(item.rate_approval_status === "requested" ||
                                  item.rate_approval_status === "rejected") && (
                                  <button
                                    type="button"
                                    onClick={() => openAction(item.retailer_id || item._id, "approve")}
                                    style={{
                                      background: "linear-gradient(135deg, #28a745, #20c997)",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      padding: "6px 14px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Approve
                                  </button>
                                )}
                                {(item.rate_approval_status === "requested" ||
                                  item.rate_approval_status === "pending" ||
                                  item.rate_approval_status === "rejected") && (
                                  <button
                                    type="button"
                                    onClick={() => openAction(item.retailer_id || item._id, "reject")}
                                    style={{
                                      background: "#fff",
                                      color: "#dc3545",
                                      border: "1px solid #dc3545",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Message / Reject
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    history.push(
                                      "/retailer-details?id=" + (item.retailer_id || item._id)
                                    )
                                  }
                                  style={{
                                    background: "transparent",
                                    color: "#0323fe",
                                    border: "1px solid #0323fe",
                                    borderRadius: "6px",
                                    padding: "6px 12px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                  }}
                                >
                                  Profile
                                </button>
                              </div>
                            </td>
                          </tr>

                          {expandedRow === rowKey && (
                            <tr>
                              <td colSpan="8" style={{ padding: "12px 16px", background: "#f8fafc" }}>
                                <RateRequestProfile
                                  item={item}
                                  title="All rate requests from this retailer"
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {actionRetailer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "480px",
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <h5 style={{ marginBottom: "8px" }}>
              {actionRetailer.mode === "approve"
                ? "Approve rate setup"
                : "Send message / request update"}
            </h5>
            <p style={{ color: "#6c757d", fontSize: "13px", marginBottom: "16px" }}>
              {actionRetailer.mode === "approve"
                ? "Optional message will be emailed to the retailer. After approve they can book anytime."
                : "Required message will be emailed and booking stays blocked until you approve."}
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={
                actionRetailer.mode === "approve"
                  ? "Optional note for retailer..."
                  : "Explain what needs to be corrected..."
              }
              style={{
                width: "100%",
                border: "1px solid #ced4da",
                borderRadius: "8px",
                padding: "12px",
                fontSize: "14px",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
              <button
                type="button"
                onClick={closeAction}
                disabled={submitting}
                style={{
                  border: "1px solid #ced4da",
                  background: "#fff",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitAction}
                disabled={submitting}
                style={{
                  border: "none",
                  background: actionRetailer.mode === "approve" ? "#28a745" : "#dc3545",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: "700",
                }}
              >
                {submitting
                  ? "Saving..."
                  : actionRetailer.mode === "approve"
                    ? "Confirm Approve"
                    : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
